import { CuisineCarousel } from 'components/carousels/cuisine-carousel';
import { DietaryCarousel } from 'components/carousels/dietary-carousel';
import { DishesCarousel } from 'components/carousels/dishes-carousel';
import { ShefsCarousel } from 'components/carousels/shefs-carousel';
import { BrowseAll } from 'components/city-blocks/browse-all';
import { HeaderCopy } from 'components/city-blocks/header-copy';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { describeFilter } from 'components/dynamic-content/copy-util';
import {
  generateCityPageH2,
  generateCityPageH2CopyBlock,
  generateCityPageMetaDesc,
} from 'components/dynamic-content/header-copy/city-page-copy';
import { generateHeroText } from 'components/dynamic-content/hero-copy';
import {
  generateCuisinePositioningCopy,
  generateDishesPositioningCopy,
  generateShefsPositioningCopy,
} from 'components/dynamic-content/positioning-copy';
import { generateCityPageTitle } from 'components/dynamic-content/title-copy';
import { WavyDivider } from 'components/flair/WavyDivider';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import {
  generateChefSchema,
  generateDishSchema,
  generateLocalBusinessSchema,
  generateServiceSchema,
  SchemaMarkup,
  SEOHead,
} from 'components/seo';
import React from 'react';
import { getAssetPath } from 'utils/asset-utils';
import { Crumb, generateCityPageBreadcrumbs } from 'utils/breadcrumb-util';
import { CuisineFilterButton } from 'utils/cuisine-util';
import { DietaryFilterButton } from 'utils/dietary-util';
import { getFrontEndUrl } from 'utils/environment-util';
import { CityPageDishFragment, CityPageFilterFragment, CityPageShefFragment } from 'utils/gqlReactTypings.generated.d';
import { ShallowCityPage } from 'utils/types';
import { encodeCityPageHref } from 'utils/urls';

interface IProps {
  cityPage: ShallowCityPage;
  dishes?: CityPageDishFragment[];
  shefs?: CityPageShefFragment[];
  totalDishesCount?: number;
  cuisineButtons: CuisineFilterButton[];
  dietaryButtons: DietaryFilterButton[];
  activeFilter?: CityPageFilterFragment;
}

export const CityPageLayout: React.FC<IProps> = ({
  cityPage,
  dishes,
  shefs,
  totalDishesCount,
  cuisineButtons,
  dietaryButtons,
  activeFilter,
}) => {
  const cityState = `${cityPage.cityName}, ${cityPage.stateNameShort}`;
  const explorePageLink = `${getFrontEndUrl()}/explore/zip/${cityPage.zipCode}?utm_source=seo-city-pages`;
  const siteUrl = getFrontEndUrl();
  const pageUrl = `${siteUrl}${encodeCityPageHref(cityPage, activeFilter)}`;

  const crumbs: Crumb[] = generateCityPageBreadcrumbs(cityPage, activeFilter);

  const title = generateCityPageTitle(cityPage, activeFilter);
  const description = generateCityPageMetaDesc({ cityPage, activeFilter });
  const numCuisines = cuisineButtons.length;

  // Generate schemas for city page
  const schemas = [
    generateLocalBusinessSchema({
      name: `Shef - Homemade Food Delivery in ${cityPage.cityName}`,
      description: `Order authentic homemade meals from local home chefs in ${cityState}`,
      url: pageUrl,
      address: {
        addressLocality: cityPage.cityName,
        addressRegion: cityPage.stateNameShort,
        postalCode: cityPage.zipCode,
        addressCountry: 'US',
      },
      areaServed: cityPage.cityName,
    }),
    generateServiceSchema(cityPage.cityName, cityPage.stateNameShort),
    // Add top dishes schemas (limit to 3 for performance)
    ...(dishes?.slice(0, 3).map((dish) => generateDishSchema(dish)) || []),
    // Add top chefs schemas (limit to 3 for performance)
    ...(shefs?.slice(0, 3).map((chef) => generateChefSchema(chef)) || []),
  ];

  // Generate keywords based on city and cuisine
  const { filterDescription, hasFoodOrGoods } = describeFilter(activeFilter);
  const keywords = [
    `${cityPage.cityName} food delivery`,
    `homemade food ${cityPage.cityName}`,
    `local chefs ${cityPage.cityName}`,
    ...(filterDescription
      ? [`${`${filterDescription} ${hasFoodOrGoods ? '' : 'Food'} ${cityPage.cityName}`.trim()}`]
      : []),
  ];

  return (
    <Layout>
      <SEOHead title={title} description={description} url={pageUrl} keywords={keywords} canonical={pageUrl} />
      <SchemaMarkup schemas={schemas} id='city-page-schema' />
      <HeroBlock
        className='mb-10'
        heroText={generateHeroText(cityState, activeFilter)}
        crumbs={crumbs}
        backgroundImage={getAssetPath('/images/CDN/cities_banner.png')}
        zipCode={cityPage.zipCode}
      />
      <MaxWidthContainer className='px-4 md:px-0'>
        <HeaderCopy
          className='mb-10'
          title={generateCityPageH2({ cityPage, activeFilter })}
          content={generateCityPageH2CopyBlock({
            cityPage,
            cuisineButtons,
            dishes,
            numDishes: totalDishesCount,
            activeFilter,
          })}
        />
        <WavyDivider className='pb-10' />
      </MaxWidthContainer>
      <CuisineCarousel
        id='cuisine-carousel'
        className='mb-10'
        cuisineButtons={cuisineButtons}
        activeFilter={activeFilter}
        title={`Popular Cuisines in ${cityPage.cityName} `}
        positioningCopy={generateCuisinePositioningCopy(cityPage, activeFilter)}
      />
      <DietaryCarousel
        id='dietary-carousel'
        className='mb-20'
        dietaryButtons={dietaryButtons}
        title='Dietary Filters'
        activeFilter={activeFilter}
      />
      <DishesCarousel
        className='mb-20'
        dishes={dishes}
        positioningCopy={generateDishesPositioningCopy({
          cityPage,
          numCuisines,
          numDishes: totalDishesCount,
          activeFilter,
        })}
        seeAllHref={explorePageLink}
        activeFilter={activeFilter}
      />
      <ShefsCarousel
        className='mb-20'
        shefs={shefs}
        positioningCopy={generateShefsPositioningCopy(cityPage, activeFilter)}
        seeAllHref={explorePageLink}
        activeFilter={activeFilter}
      />
      <BrowseAll explorePageLink={explorePageLink} />
    </Layout>
  );
};
